import type { EmailSettings, PaletteEntry } from './types'

// Palette, in the prototype's order (ED_PALETTE). `soon` entries show a
// "Coming soon" tag and can't be dragged or clicked; `hidden` ones are omitted.
export const PALETTE: PaletteEntry[] = [
  // Layout
  {
    key: 'col1',
    label: '1 column',
    group: 'Layout',
    type: 'columns',
    init: { ratio: '100' },
    dynamic: false,
    status: 'ready',
    icon: 'col1',
  },
  {
    key: 'col2',
    label: '2 columns',
    group: 'Layout',
    type: 'columns',
    init: { ratio: '50-50' },
    dynamic: false,
    status: 'ready',
    icon: 'col2',
  },
  {
    key: 'col3',
    label: '3 columns',
    group: 'Layout',
    type: 'columns',
    init: { ratio: '33-33-33' },
    dynamic: false,
    status: 'ready',
    icon: 'col3',
  },
  {
    key: 'divider',
    label: 'Divider',
    group: 'Layout',
    type: 'divider',
    dynamic: false,
    status: 'ready',
    icon: 'divider',
  },
  {
    key: 'spacer',
    label: 'Spacer',
    group: 'Layout',
    type: 'spacer',
    dynamic: false,
    status: 'ready',
    icon: 'spacer',
  },
  // Content
  {
    key: 'heading',
    label: 'Heading',
    group: 'Content',
    type: 'text',
    init: { preset: 'h1', size: 30, html: 'Your headline here' },
    dynamic: false,
    status: 'ready',
    icon: 'heading',
  },
  {
    key: 'text',
    label: 'Text',
    group: 'Content',
    type: 'text',
    dynamic: false,
    status: 'ready',
    icon: 'text',
  },
  {
    key: 'image',
    label: 'Image',
    group: 'Content',
    type: 'image',
    dynamic: false,
    status: 'ready',
    icon: 'image',
  },
  {
    key: 'split',
    label: 'Image + text',
    group: 'Content',
    type: 'split',
    dynamic: false,
    status: 'ready',
    icon: 'split',
  },
  {
    key: 'button',
    label: 'Button',
    group: 'Content',
    type: 'button',
    dynamic: false,
    status: 'ready',
    icon: 'button',
  },
  {
    key: 'video',
    label: 'Video',
    group: 'Content',
    type: 'video',
    dynamic: false,
    status: 'hidden',
    icon: 'video',
  },
  {
    key: 'table',
    label: 'Table',
    group: 'Content',
    type: 'table',
    dynamic: false,
    status: 'ready',
    icon: 'table',
  },
  {
    key: 'html',
    label: 'HTML',
    group: 'Content',
    type: 'html',
    dynamic: false,
    status: 'hidden',
    icon: 'html',
  },
  {
    key: 'social',
    label: 'Social',
    group: 'Content',
    type: 'social',
    dynamic: false,
    status: 'ready',
    icon: 'social',
  },
  // Store
  {
    key: 'header',
    label: 'Header',
    group: 'Store',
    type: 'header',
    dynamic: false,
    status: 'ready',
    icon: 'header',
  },
  {
    key: 'product',
    label: 'Products',
    group: 'Store',
    type: 'product',
    dynamic: true,
    status: 'ready',
    icon: 'product',
  },
  {
    key: 'coupon',
    label: 'Coupon',
    group: 'Store',
    type: 'coupon',
    dynamic: false,
    status: 'ready',
    icon: 'coupon',
  },
  {
    key: 'footer',
    label: 'Footer',
    group: 'Store',
    type: 'footer',
    dynamic: false,
    status: 'ready',
    icon: 'footer',
  },
  // Loyalty & dynamic
  {
    key: 'wallet',
    label: 'Wallet',
    group: 'Loyalty & dynamic',
    type: 'wallet',
    dynamic: true,
    status: 'soon',
    icon: 'wallet',
  },
  {
    key: 'rewards',
    label: 'Rewards',
    group: 'Loyalty & dynamic',
    type: 'rewards',
    dynamic: true,
    status: 'soon',
    icon: 'rewards',
  },
  {
    key: 'tierbar',
    label: 'Tier progress',
    group: 'Loyalty & dynamic',
    type: 'tierbar',
    dynamic: true,
    status: 'soon',
    icon: 'tier',
  },
  {
    key: 'recommended',
    label: 'For you',
    group: 'Loyalty & dynamic',
    type: 'product',
    init: {
      source: 'recommended',
      btnLabel: 'View product',
      title: 'Picked for you, {{first_name}}',
      showTitle: true,
    },
    dynamic: true,
    status: 'soon',
    icon: 'spark',
  },
  {
    key: 'cart',
    label: 'Cart items',
    group: 'Loyalty & dynamic',
    type: 'cart',
    dynamic: true,
    status: 'ready',
    icon: 'cart',
  },
  {
    key: 'countdown',
    label: 'Countdown',
    group: 'Loyalty & dynamic',
    type: 'countdown',
    dynamic: true,
    status: 'soon',
    icon: 'clock',
  },
]

// Status of a concrete block (a product block whose source is 'recommended'
// is the "For you" entry, so it shares that entry's status).
export const blockStatus = (b: {
  type: string
  props?: Record<string, any>
}): PaletteEntry['status'] => {
  if (b.type === 'product' && b.props?.source === 'recommended') return 'soon'
  const entry = PALETTE.find(
    (p) => p.type === b.type && p.key !== 'recommended',
  )
  return entry ? entry.status : 'hidden'
}

// Human label per block type (block chrome tab, panel header).
export const BLOCK_LABELS: Record<string, string> = {
  columns: 'Columns',
  text: 'Text',
  image: 'Image',
  split: 'Image + text',
  button: 'Button',
  divider: 'Divider',
  spacer: 'Spacer',
  table: 'Table',
  social: 'Social links',
  header: 'Header',
  footer: 'Footer',
  product: 'Products',
  coupon: 'Coupon',
  wallet: 'Loyalty wallet',
  cart: 'Cart items',
  tierbar: 'Tier progress',
  rewards: 'Rewards',
  countdown: 'Countdown',
  video: 'Video',
  html: 'HTML',
}

// Email-safe font stacks (prototype ED_FONTS).
export const FONT_STACKS: string[] = [
  'Helvetica, Arial, sans-serif',
  'Verdana, Geneva, sans-serif',
  "'Trebuchet MS', Tahoma, sans-serif",
  "Georgia, 'Times New Roman', serif",
  "'Courier New', Courier, monospace",
]

export const FONT_LABELS: Record<string, string> = {
  'Helvetica, Arial, sans-serif': 'Helvetica / Arial',
  'Verdana, Geneva, sans-serif': 'Verdana',
  "'Trebuchet MS', Tahoma, sans-serif": 'Trebuchet',
  "Georgia, 'Times New Roman', serif": 'Georgia',
  "'Courier New', Courier, monospace": 'Courier',
}

// Text presets (prototype ED_PRESETS).
export const TEXT_PRESETS: Record<
  string,
  { size: number; weight: number; lh: number }
> = {
  h1: { size: 30, weight: 800, lh: 1.2 },
  h2: { size: 22, weight: 700, lh: 1.3 },
  p: { size: 15, weight: 400, lh: 1.6 },
  small: { size: 12, weight: 400, lh: 1.5 },
}

// Column ratios the compiler understands.
export const COLUMN_RATIOS = [
  '100',
  '50-50',
  '33-67',
  '67-33',
  '33-33-33',
  '25-50-25',
] as const

// Social networks (prototype ED_NETS): [label, glyph].
export const SOCIAL_NETS: Record<string, [string, string]> = {
  instagram: ['Instagram', 'IG'],
  facebook: ['Facebook', 'f'],
  x: ['X', 'X'],
  tiktok: ['TikTok', 'TT'],
  youtube: ['YouTube', '▶'],
}

export const DEFAULT_BRAND = '#1E7D4A'

const HEX6 = /^#[0-9a-fA-F]{6}$/
const HEX3 = /^#[0-9a-fA-F]{3}$/

// A #rrggbb colour (a #rgb is expanded), else the fallback.
const hexOr = (v: unknown, fallback: string): string => {
  const s = typeof v === 'string' ? v.trim() : ''
  if (HEX6.test(s)) return s
  if (HEX3.test(s)) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`
  return fallback
}

// What the server accepts for email_settings: #rrggbb colours, a width of
// 480-720 and one of the editor's font stacks. Anything else (e.g. a colour
// the user cleared) falls back to the defaults so a save never 400s.
export function sanitizeEmailSettings(
  settings: Partial<EmailSettings> | null | undefined,
): EmailSettings {
  const d = defaultSettings()
  const s = settings ?? {}
  const width = Number(s.width)
  return {
    bg: hexOr(s.bg, d.bg),
    body: hexOr(s.body, d.body),
    text: hexOr(s.text, d.text),
    brand: hexOr(s.brand, d.brand),
    width: Number.isFinite(width)
      ? Math.max(480, Math.min(720, Math.round(width)))
      : d.width,
    font: FONT_STACKS.includes(s.font as string)
      ? (s.font as string)
      : FONT_STACKS[0],
  }
}

export function defaultSettings(brand?: string): EmailSettings {
  return {
    bg: '#F3F4F6',
    body: '#FFFFFF',
    width: 600,
    font: FONT_STACKS[0],
    text: '#1F2937',
    brand: brand || DEFAULT_BRAND,
  }
}
